/**
 * New console — command palette.
 *
 * Ctrl+K anywhere. It searches three things at once, in the order they are most
 * likely wanted: pages, clinics, and actions.
 *
 * Clinics are in it because the alternative is scanning a select of forty-six names
 * in the topbar. Typing three letters and pressing Enter opens that clinic's
 * workspace and scopes the rest of the console to it.
 *
 * Matching is a plain substring on a lowercased haystack rather than a fuzzy score.
 * Fuzzy matching looks clever and ranks "Diagnose" above "Clinics" for the query
 * "clin"; substring order is boring and always explicable.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { IconSearch, IconClinic, IconCog, IconMoon, IconSun, IconLock } from './Icons';

const host = () => document.querySelector('.nt') || document.body;

const CommandPalette = ({
  routes, clinics, onClose, onGo, onPickClinic, onToggleTheme, isDark, onShortcuts, onLock
}) => {
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const items = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const hit = (s) => !needle || String(s).toLowerCase().indexOf(needle) >= 0;
    const out = [];

    /* The meta shows the chord where one exists — the palette is where somebody learns
       that "g l" would have been quicker. */
    routes.filter((r) => hit(r.label)).forEach((r) => out.push({
      sec: 'Pages', label: r.label, meta: r.key ? `G then ${r.key.toUpperCase()}` : r.id, Icon: r.icon, run: () => onGo(r.id)
    }));

    /* Capped: forty-six clinics with an empty query would bury the pages under a
       list nobody opened the palette to read. */
    clinics.filter((c) => hit(c.name)).slice(0, needle ? 12 : 4).forEach((c) => out.push({
      sec: 'Clinics', label: c.name, meta: 'Open workspace', Icon: IconClinic, run: () => {
        onPickClinic(c.id);
        onGo(`/clinic/${c.id}`);
      }
    }));

    if (clinics.length && hit('all clinics')) {
      out.push({ sec: 'Clinics', label: 'All clinics', meta: 'Clear the scope', Icon: IconClinic, run: () => onPickClinic(0) });
    }

    const actions = [
      { label: isDark ? 'Switch to light theme' : 'Switch to dark theme', Icon: isDark ? IconSun : IconMoon, meta: 'T', run: onToggleTheme },
      { label: 'Keyboard shortcuts', Icon: IconCog, meta: '?', run: onShortcuts },
      { label: 'Lock the screen', Icon: IconLock, meta: 'Blanks the console; your password brings it back', run: onLock }
    ];
    actions.filter((a) => hit(a.label)).forEach((a) => out.push({ sec: 'Actions', ...a }));

    return out;
  }, [q, routes, clinics, onGo, onPickClinic, onToggleTheme, isDark, onShortcuts, onLock]);

  useEffect(() => { setSel(0); }, [q]);
  useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, []);

  useEffect(() => {
    const node = listRef.current && listRef.current.querySelector('[aria-selected="true"]');
    if (node && node.scrollIntoView) node.scrollIntoView({ block: 'nearest' });
  }, [sel]);

  const run = (i) => {
    const it = items[i];
    if (!it) return;
    onClose();
    it.run();
  };

  const onKey = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(items.length - 1, s + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(0, s - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); run(sel); }
  };

  let section = '';

  return createPortal(
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div className="nt-scrim-full cmdk" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="nt-cmdk" role="dialog" aria-modal="true" aria-label="Command palette">
        <div className="nt-cmdk-in">
          <IconSearch />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="Search pages, clinics or actions…"
            aria-label="Search"
            autoComplete="off"
          />
          <span className="nt-kbd">Esc</span>
        </div>
        <div className="nt-cmdk-list" ref={listRef} role="listbox" aria-label="Results">
          {!items.length ? (
            <div className="nt-empty" style={{ padding: 30 }}>
              <h4>Nothing matches</h4>
              <p>Try a clinic name or a page.</p>
            </div>
          ) : items.map((it, i) => {
            const head = it.sec !== section ? it.sec : null;
            section = it.sec;
            const Icon = it.Icon;
            return (
              <div key={`${it.sec}:${it.label}`}>
                {head ? <div className="nt-cmdk-sec nt-eyebrow">{head}</div> : null}
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
                <div
                  className="nt-cmdk-item"
                  role="option"
                  aria-selected={i === sel}
                  onMouseEnter={() => setSel(i)}
                  onClick={() => run(i)}
                >
                  {Icon ? <Icon /> : null}
                  <span className="lab">{it.label}</span>
                  {it.meta ? <span className="meta">{it.meta}</span> : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    host()
  );
};

export default CommandPalette;
